import { CheckOutlined } from '@ant-design/icons';
import { Button, Modal, Select, Typography, theme } from 'antd';
import { useState, type CSSProperties, type JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { ModalSelectOption } from './ModalSelect';

interface ModalMultiSelectProps {
  value?: string[];
  /** Form-controlled path: Ant Form injects value/onChange. */
  onChange?: (value: string[]) => void;
  options: ModalSelectOption[];
  placeholder?: string;
  /** Modal heading; falls back to the placeholder. */
  title?: string;
  loading?: boolean;
  disabled?: boolean;
  size?: 'small' | 'middle' | 'large';
  style?: CSSProperties;
}

/**
 * Multi-value sibling of ModalSelect: the trigger is a display-only tag
 * `Select` (never opens its own dropdown, no typing/search input), and the
 * options open in a Modal as a tap-to-toggle checklist. Selections apply
 * immediately; «Готово» just closes, «Очистить» empties the list.
 */
export function ModalMultiSelect({
  value,
  onChange,
  options,
  placeholder,
  title,
  loading,
  disabled,
  size = 'large',
  style,
}: ModalMultiSelectProps): JSX.Element {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const [open, setOpen] = useState(false);
  const selected = value ?? [];

  const openModal = (): void => {
    if (!disabled) setOpen(true);
  };
  const close = (): void => setOpen(false);

  const toggle = (v: string): void => {
    onChange?.(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  };

  return (
    <>
      <Select
        mode="multiple"
        open={false}
        showSearch={false}
        allowClear={false}
        // No typing: the tag field is display-only, the modal does the picking.
        searchValue=""
        value={selected}
        options={options}
        placeholder={placeholder}
        size={size}
        style={style}
        loading={loading}
        disabled={disabled}
        onDeselect={(v) => toggle(String(v))}
        onClick={(e) => {
          // Tapping a tag's «×» removes that store only — don't also open the picker.
          const el = e.target as HTMLElement;
          if (el.closest('.ant-select-selection-item-remove')) return;
          openModal();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openModal();
          }
        }}
      />
      <Modal
        open={open}
        onCancel={close}
        title={title ?? placeholder ?? t('common.select')}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <Button size={size} onClick={() => onChange?.([])} disabled={selected.length === 0}>
              {t('common.clear')}
            </Button>
            <Button size={size} type="primary" onClick={close}>
              {t('common.done')}
            </Button>
          </div>
        }
        destroyOnClose
        // Sit above any Drawer/Modal this picker was opened from (both 1000).
        zIndex={1100}
      >
        <div
          style={{
            maxHeight: '60vh',
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            WebkitOverflowScrolling: 'touch',
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: '24px 4px', textAlign: 'center' }}>
              <Typography.Text type="secondary">{t('common.notFound')}</Typography.Text>
            </div>
          ) : (
            options.map((o) => {
              const isOn = selected.includes(o.value);
              return (
                <div
                  key={o.value}
                  onClick={() => {
                    if (!o.disabled) toggle(o.value);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    padding: '12px 4px',
                    borderTop: `1px solid ${token.colorBorderSecondary}`,
                    cursor: o.disabled ? 'not-allowed' : 'pointer',
                    color: o.disabled
                      ? token.colorTextDisabled
                      : isOn
                        ? token.colorPrimary
                        : token.colorText,
                    fontWeight: isOn ? 600 : 400,
                  }}
                >
                  <span>{o.label}</span>
                  {isOn ? <CheckOutlined /> : null}
                </div>
              );
            })
          )}
        </div>
      </Modal>
    </>
  );
}
